import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Skeleton
} from '@mui/material';
import CompetenciaStorageUtils from '../../Storages/competenciaStorage';
import { getToken as getAuthToken } from '../../Storages/cookies';
import EstablishmentStorageUtils from '../../Storages/establishmentStorage';

interface CompetenciaItem {
  sped_fiscal_id: number;
  competencia: string;
  data_inicio: string;
  data_fim: string;
}

interface Props {
  onChangeSpedFiscalId: (id: number | null, dataInicio?: string, dataFim?: string) => void;
  isDashboard?: boolean;
  /** Quando true, o componente renderiza apenas o conteúdo interno, sem o Card wrapper */
  disableCard?: boolean;
}

const Competencia: React.FC<Props> = ({ onChangeSpedFiscalId, isDashboard = false, disableCard = false }): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [razaoSocial, setRazaoSocial] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [competenciaSelecionada, setCompetenciaSelecionada] = useState<string>('');
  const [competencias, setCompetencias] = useState<CompetenciaItem[]>([]);
  const [tentativaBuscarCompetencia, setTentativaBuscarCompetencia] = useState<number>(0);

  const normalizeId = (value: number | string | null | undefined): string =>
    value === null || value === undefined ? '' : String(value);

  const parseNumericId = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const checkSpedStatus = async (spedId: number): Promise<string> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/ultimo/${spedId}`);
      const data = await response.json();
      return data.status || '';
    } catch (error) {
      console.error('Erro ao verificar status do SPED:', error);
      return '';
    }
  };

  const aplicarCompetenciaSalva = useCallback(() => {
    const competenciaSalva = CompetenciaStorageUtils.get();
    if (!competenciaSalva?.sped_fiscal_id) return;

    const normalizedId = normalizeId(competenciaSalva.sped_fiscal_id);
    setCompetenciaSelecionada(normalizedId);

    const numericId = parseNumericId(competenciaSalva.sped_fiscal_id);
    if (numericId !== null) {
      onChangeSpedFiscalId(numericId, competenciaSalva.data_inicio, competenciaSalva.data_fim);
    }
  }, [onChangeSpedFiscalId]);

  useEffect(() => {
    aplicarCompetenciaSalva();
  }, [aplicarCompetenciaSalva]);

  const fetchData = useCallback(async () => {
    try {
      const totalResponse = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/total`);
      const totalData = await totalResponse.json();

      if (totalData?.total > 0) {
        const empresaRes = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/get-dados-empresa`);
        const empresaData = await empresaRes.json();

        // Verifica se os dados da empresa estão realmente disponíveis
        const temDadosEmpresa = empresaData?.razao_social && empresaData?.cnpj;
        
        if (temDadosEmpresa) {
          setRazaoSocial(empresaData.razao_social);
          setCnpj(empresaData.cnpj);
        } else if (isDashboard) {
          // Se é Dashboard e não tem dados da empresa ainda, continua tentando
          setTimeout(() => setTentativaBuscarCompetencia(n => n+1), 2000);
          return; // Sai da função sem continuar
        }

        const token = getAuthToken();

        // Captura estabelecimento selecionado (se existir)
        const establishmentSelecionado = EstablishmentStorageUtils.get();
        const establishmentId = establishmentSelecionado?.establishment_id;

        const endpointBase = `${process.env.REACT_APP_API_URL}sped-fiscal/competencias`;
        const endpoint = establishmentId ? `${endpointBase}/${establishmentId}` : endpointBase;

        console.log('[Competencia] endpoint', endpoint);
        const competenciasRes = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
          credentials: 'include'
        });

        const competenciasData = await competenciasRes.json();
        console.log('[Competencia] raw competencias', competenciasData);
        if (Array.isArray(competenciasData?.data)) {
          // Filtrar competências que realmente têm NF-e (total-nfes > 0)
          const competenciasRaw: CompetenciaItem[] = competenciasData.data;
          const competenciasFiltradas: CompetenciaItem[] = [];

          for (const comp of competenciasRaw) {
            try {
              const totalRes = await fetch(
                `${process.env.REACT_APP_API_URL}sped-fiscal/total-nfes/${comp.sped_fiscal_id}`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                  },
                  credentials: 'include'
                }
              );
              const totalData = await totalRes.json();
              const totalEntrada = Number(totalData?.total_entrada ?? 0);
              const totalSaida = Number(totalData?.total_saida ?? 0);
              const ok = totalRes.ok && (totalEntrada > 0 || totalSaida > 0);
              console.log('[Competencia] valida NF-e', comp.sped_fiscal_id, { status: totalRes.status, totalEntrada, totalSaida, ok });
              if (ok) {
                competenciasFiltradas.push(comp);
              }
            } catch (e) {
              console.warn('[Competencia] Falha ao validar total-nfes', comp.sped_fiscal_id, e);
            }
          }

          setCompetencias(competenciasFiltradas);
          
          if (competenciasFiltradas.length > 0) {
            let competenciaParaSelecionar: CompetenciaItem | null = null;

            // Primeiro, tenta recuperar competência salva no localStorage
              const competenciaSalva = CompetenciaStorageUtils.get();
            if (competenciaSalva?.sped_fiscal_id) {
              const savedId = normalizeId(competenciaSalva.sped_fiscal_id);
              const competenciaEncontrada = competenciasFiltradas.find(
                (comp: CompetenciaItem) => normalizeId(comp.sped_fiscal_id) === savedId
              );
              if (competenciaEncontrada) {
                competenciaParaSelecionar = competenciaEncontrada;
              } else {
                // Se a salva não existe mais, limpa o storage
                CompetenciaStorageUtils.clear();
              }
            }

            // Se não encontrou competência salva ou ela não existe mais, usa lógica padrão
              if (!competenciaParaSelecionar) {
                if (isDashboard) {
                  // Para o Dashboard, prioriza competências com status "Realizando importação"
                  for (const comp of competenciasFiltradas) {
                    const status = await checkSpedStatus(comp.sped_fiscal_id);
                    if (status === 'Realizando importação') {
                      competenciaParaSelecionar = comp;
                      break;
                    }
                  }
                  
                  // Se não encontrou nenhuma "Realizando importação", pega a primeira
                  if (!competenciaParaSelecionar) {
                    competenciaParaSelecionar = competenciasFiltradas[0];
                  }
                } else {
                  // Para outras páginas, comportamento normal (primeira da lista)
                  competenciaParaSelecionar = competenciasFiltradas[0];
                }
              }

              if (competenciaParaSelecionar) {
                const normalizedId = normalizeId(competenciaParaSelecionar.sped_fiscal_id);
                const numericId = parseNumericId(competenciaParaSelecionar.sped_fiscal_id);
                setCompetenciaSelecionada(normalizedId);
                
                if (numericId !== null) {
                  // Salva a competência selecionada no localStorage
                  CompetenciaStorageUtils.save({
                    sped_fiscal_id: numericId,
                    competencia: competenciaParaSelecionar.competencia,
                    data_inicio: competenciaParaSelecionar.data_inicio,
                    data_fim: competenciaParaSelecionar.data_fim
                  });

                  onChangeSpedFiscalId(
                    numericId, 
                    competenciaParaSelecionar.data_inicio, 
                    competenciaParaSelecionar.data_fim
                  );
                } else {
                  console.warn('Não foi possível interpretar o ID da competência selecionada.', competenciaParaSelecionar.sped_fiscal_id);
                }
              }
          } else {
            // Só faz retry se for Dashboard
            if (isDashboard) {
              setTimeout(() => setTentativaBuscarCompetencia(n => n+1), 2000);
            }
            setCompetenciaSelecionada('');
            onChangeSpedFiscalId(null);
          }
        } else {
          setCompetencias([]);
          setCompetenciaSelecionada('');
          onChangeSpedFiscalId(null);
        }
      } else {
        // Se não há SPEDs e é Dashboard, continua tentando
        if (isDashboard) {
          setTimeout(() => setTentativaBuscarCompetencia(n => n+1), 3000);
        }
        setCompetencias([]);
        setCompetenciaSelecionada('');
        onChangeSpedFiscalId(null);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de competências:', error);
      
      // Se é Dashboard e houve erro, continua tentando
      if (isDashboard) {
        setTimeout(() => setTentativaBuscarCompetencia(n => n+1), 5000);
      }
      
      setCompetencias([]);
      setCompetenciaSelecionada('');
      onChangeSpedFiscalId(null);
    } finally {
      setLoading(false);
    }
  }, [onChangeSpedFiscalId, isDashboard]);

  useEffect(() => {
    fetchData();
  }, [fetchData, tentativaBuscarCompetencia]);

    const handleChange = (event: SelectChangeEvent<string>) => {
      const selected = event.target.value as string;
      setCompetenciaSelecionada(selected);
      const competenciaEncontrada = competencias.find(
        c => normalizeId(c.sped_fiscal_id) === selected
      );

      const numericId = parseNumericId(competenciaEncontrada?.sped_fiscal_id ?? selected);
      
      if (competenciaEncontrada && numericId !== null) {
        // Salva a nova competência selecionada no localStorage
        CompetenciaStorageUtils.save({
          sped_fiscal_id: numericId,
          competencia: competenciaEncontrada.competencia,
          data_inicio: competenciaEncontrada.data_inicio,
          data_fim: competenciaEncontrada.data_fim
        });
      }

      // Busca dados da empresa referentes à nova competência
      (async () => {
        if (!numericId) {
          setRazaoSocial('Buscando Razão Social da empresa...');
          setCnpj('Buscando CNPJ da empresa...');
          return;
        }
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/get-dados-empresa/${numericId}`);
          const data = await res.json();
          if (data?.razao_social && data?.cnpj) {
            setRazaoSocial(data.razao_social);
            setCnpj(data.cnpj);
          }
        } catch (err) {
          console.error('Erro ao buscar dados da empresa:', err);
        }
      })();

      if (numericId !== null) {
        onChangeSpedFiscalId(numericId, competenciaEncontrada?.data_inicio, competenciaEncontrada?.data_fim);
      } else {
        console.warn('ID de competência inválido selecionado.', selected);
      }
  };

  if ((competencias?.length || 0) === 0 && isDashboard) {
    // Dashboard: exibe skeleton enquanto busca competências
    const skeletonContent = (
      <Box sx={{ p: 1 }}>
        <Skeleton variant="text" width="120px" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
      </Box>
    );

    return disableCard ? skeletonContent : (
      <Card>
        <CardContent>{skeletonContent}</CardContent>
      </Card>
    );
  }

  const selectContent = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
    >
      {loading ? (
        <Box sx={{ minWidth: 240 }}>
          <Skeleton variant="text" width="120px" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
        </Box>
      ) : (
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="competencia-label">Competência</InputLabel>
            <Select
            labelId="competencia-label"
              value={competenciaSelecionada}
            label="Competência"
            onChange={handleChange}
            disabled={competencias.length === 0}
          >
            {competencias.length > 0 ? (
              competencias.map(({ sped_fiscal_id, competencia }) => (
                  <MenuItem key={sped_fiscal_id} value={normalizeId(sped_fiscal_id)}>
                  {competencia}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                Nenhum SPED disponível
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}
    </Box>
  );

  return disableCard ? selectContent : (
    <Card>
      <CardContent>{selectContent}</CardContent>
    </Card>
  );
};

export default Competencia;
